import { QueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { CookieKey, LocalStorageKey } from '@common/ts';
import Cookies from 'js-cookie';
import useUserStore from './UserStore';
import Routes from '@/router/routes';

type State = {
  authToken: string | null;
  requireTwoFactor: boolean;
  twoFactorUserId: string | null;
  isLoggingOut: boolean;
};

type Action = {
  setAuthToken: (token: string | null) => void;
  setRequireTwoFactor: (require: boolean) => void;
  setTwoFactorUserId: (userId: string | null) => void;
  clearTwoFactorState: () => void;
  logout: (navigate?: (path: string) => void, queryClient?: QueryClient) => void;
  setIsLoggingOut: (value: boolean) => void;
};

const useAuthStore = create<State & Action>((set) => ({
  authToken: Cookies.get(CookieKey.TOKEN) || null,
  requireTwoFactor: false,
  twoFactorUserId: null,
  isLoggingOut: false,

  setAuthToken: (token) => {
    if (token) {
      Cookies.set(CookieKey.TOKEN, token);
    } else {
      Cookies.remove(CookieKey.TOKEN);
    }
    set({ authToken: token });
  },

  setRequireTwoFactor: (require) => set({ requireTwoFactor: require }),

  setTwoFactorUserId: (userId) => set({ twoFactorUserId: userId }),

  clearTwoFactorState: () => set({ requireTwoFactor: false, twoFactorUserId: null }),

  logout: (navigate, queryClient) => {
    const loginPage = Routes.login.path;
    // First set isLoggingOut to true to prevent redirection loops
    set({ isLoggingOut: true });

    // Clear all auth-related cookies
    Cookies.remove(CookieKey.TOKEN);
    Cookies.remove(CookieKey.MAIN_ORGANIZATION);
    Cookies.remove(CookieKey.REMEMBER_ME);
    Cookies.remove(CookieKey.FALLBACK_WORKSPACE);

    // Clear user store
    useUserStore.getState().clearUser();

    // Clear React Query cache to prevent stale user data
    if (queryClient) {
      queryClient.clear();
    }

    // Preserve clientIP when clearing localStoragess
    const clientIP = localStorage.getItem(LocalStorageKey.CLIENT_IP);
    localStorage.clear();
    if (clientIP) {
      localStorage.setItem(LocalStorageKey.CLIENT_IP, clientIP);
    }

    // Clear store state
    set({
      authToken: null,
      requireTwoFactor: false,
      twoFactorUserId: null,
      isLoggingOut: false,
    });

    // Redirect to login page
    if (navigate) {
      navigate(loginPage);
    } else {
      window.location.href = loginPage;
    }
  },

  setIsLoggingOut: (value) => set({ isLoggingOut: value }),
}));

export default useAuthStore;
